/**
 * @module Services/exportExcel
 * @description Global Excel export helpers using SheetJS (xlsx).
 */
import * as XLSX from 'xlsx';
import { openLocalXlsx } from '@/Services/docOpeners';

/**
 * Strip vue-good-table’s accessible sort helper and normalize whitespace.
 * Example input: "Sort table by Item in descending order" → output: "Item".
 * @param {string} raw
 * @returns {string}
 */
function cleanVgtHeaderText(raw) {
  if (!raw) return '';
  let text = String(raw).replace(/\s+/g, ' ').trim();
  const idx = text.toLowerCase().indexOf('sort table by');
  if (idx !== -1) text = text.slice(0, idx).trim();
  return text;
}

/**
 * @typedef {Object} SheetSpec
 * @property {string} name - Sheet name.
 * @property {Array<Object>|Array<Array<any>>} rows - Data rows (array of objects preferred).
 * @property {Array<string>} [columns] - Optional explicit column order for object rows.
 */

/**
 * Normalize input to array of SheetSpec.
 * Accepts arrays of objects, arrays of arrays, or a prebuilt SheetSpec.
 * @param {Array|SheetSpec|Array<SheetSpec>} input
 * @param {string} [defaultName="Data"]
 * @returns {Array<SheetSpec>}
 */
export function toSheetSpecs(input, defaultName = 'Data') {
  if (!input) return [{ name: defaultName, rows: [] }];
  if (Array.isArray(input)) {
    // Array<obj|arr> or Array<SheetSpec>
    if (input.length && input.every(r => r && (r.rows || Array.isArray(r.rows)))) {
      return input.map((s, i) => ({ name: s.name || `${defaultName}_${i + 1}`, rows: s.rows || [], columns: s.columns }));
    }
    return [{ name: defaultName, rows: input }];
  }
  if (typeof input === 'object') {
    if (input.rows) return [{ name: input.name || defaultName, rows: input.rows, columns: input.columns }];
    return [{ name: defaultName, rows: [input] }];
  }
  return [{ name: defaultName, rows: [] }];
}

/**
 * Create and download an .xlsx file from one or more sheets.
 * @param {Array<SheetSpec>|SheetSpec|Array|Object} data - Data or sheet specs.
 * @param {Object} [opts]
 * @param {string} [opts.filename] - Base filename (auto with timestamp if omitted).
 * @param {boolean} [opts.autoWidth=true] - Auto-fit column widths for object rows.
 */
export function exportExcel(data, opts = {}) {
  const { filename, autoWidth = true } = opts;
  const wb = XLSX.utils.book_new();
  const sheets = toSheetSpecs(data);

  sheets.forEach((sheet) => {
    const { name, rows, columns } = sheet;
    let ws;
    if (!rows || rows.length === 0) {
      ws = XLSX.utils.aoa_to_sheet([["No data"]]);
    } else if (Array.isArray(rows[0]) || Array.isArray(rows[0]?.cells)) {
      // Row is an array; assume AOA
      ws = XLSX.utils.aoa_to_sheet(rows);
    } else {
      // Array of objects
      const header = columns && columns.length ? columns : Object.keys(rows.reduce((acc, r) => (Object.assign(acc, r), acc), {}));
      ws = XLSX.utils.json_to_sheet(rows, { header });
      if (autoWidth) {
        const colWidths = header.map(h => ({ wch: Math.min(40, Math.max(10, String(h).length)) }));
        rows.forEach(r => header.forEach((h, i) => {
          const len = (r[h] == null ? 0 : String(r[h]).length);
          colWidths[i].wch = Math.min(60, Math.max(colWidths[i].wch, len + 2));
        }));
        ws['!cols'] = colWidths;
      }
    }
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName(name));
  });

  const stamp = new Date();
  const pad = (n) => n.toString().padStart(2, '0');
  const defaultName = filename || `export_${stamp.getFullYear()}-${pad(stamp.getMonth() + 1)}-${pad(stamp.getDate())}_${pad(stamp.getHours())}${pad(stamp.getMinutes())}${pad(stamp.getSeconds())}`;
  const ab = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return openLocalXlsx(ab, `${defaultName}.xlsx`);
}

/**
 * Sanitize sheet names to Excel-compatible format.
 * @param {string} name
 */
function safeSheetName(name) {
  return String(name || 'Sheet').replace(/[\\\/*?:\[\]]/g, ' ').slice(0, 31) || 'Sheet';
}

/**
 * Convert a HTML <table> to array of objects keyed by header text.
 * @param {HTMLTableElement} table
 * @returns {Array<Object>}
 */
export function htmlTableToObjects(table) {
  if (!table) return [];
  const theadHeaders = Array.from(table.querySelectorAll('thead th')).map(th => cleanVgtHeaderText((th.textContent || '').trim()));
  // If no thead, try first row as header
  let effectiveHeaders = theadHeaders;
  if (!effectiveHeaders.length) {
    const firstRowCells = table.querySelectorAll('tr:first-child th, tr:first-child td');
    effectiveHeaders = Array.from(firstRowCells).map((c, i) => cleanVgtHeaderText((c.textContent || '').trim()) || `Col ${i + 1}`);
  }
  const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
  const rows = (bodyRows.length ? bodyRows : Array.from(table.querySelectorAll('tr')).slice(1));
  return rows.map(tr => {
    const cells = Array.from(tr.querySelectorAll('td'));
    const obj = {};
    cells.forEach((td, i) => obj[effectiveHeaders[i] || `Col ${i + 1}`] = (td.textContent || '').trim());
    return obj;
  });
}
